import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { Button, Card, Dropdown, Skeleton, Snackbar } from "@chef/components";
import {
  BRAND_PATHS,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
  TIMEBLOCK_TYPE_IDS,
} from "@chef/constants";
import {
  isMealboxBasketType,
  isValidPostalCode,
  padPostalCode,
} from "@chef/helpers";
import { useConfirmDialog } from "@chef/hooks";
import {
  BillingQuery,
  useBillingQuery,
  useTimeblocksQuery,
  useUpdateBillingAgreementMutation,
  convertFromBillingToUpdateBillingAgreement,
  useOrdersQuery,
} from "@chef/state-management";
import { isNotEmptyArray } from "@chef/utils/array";

import { MyAccountLayout } from "../../components/MyAccountLayout";
import { AddressChangedAfterCutoffModal } from "../../components/AddressChangedAfterCutoffModal";
import {
  createDeliveryFrequencyOptions,
  createDeliveryTimeslotOptions,
} from "../../utils";

import { intl } from "./MyAccountUpdateDeliveryInformationPage.Intl";

export const MyAccountUpdateDeliveryInformationPage = () => {
  const { data: billingQuery, isLoading } = useBillingQuery();
  const billing = billingQuery?.billing;

  return (
    <MyAccountLayout page="deliveryInformation" hideBackOnMobile>
      <Card
        className="px-4 py-5 pb-12 -mx-4 -mt-10 md:m-0 col-span-full md:col-span-5 lg:p-6 self-baseline"
        noPadding
      >
        <h2 className="text-xl">
          <strong>{intl.SELECT_ADDRESS_AND_TIME}</strong>
        </h2>

        <p className="mt-3">{intl.FORM_DESCRIPTION}</p>

        {isLoading && (
          <div className="flex flex-col gap-6 mt-6">
            <div>
              <Skeleton className="w-14" />
              <Skeleton className="!h-10" />
            </div>

            <div>
              <Skeleton className="w-14" />
              <Skeleton className="!h-10" />
            </div>

            <div>
              <Skeleton className="w-14" />
              <Skeleton className="!h-10" />
            </div>

            <div className="flex flex-col gap-4 mt-10 lg:flex-row-reverse">
              <Skeleton className="!h-10 lg:w-24" />
              <Skeleton className="!h-10 lg:w-24" />
            </div>
          </div>
        )}

        {billing && <Form billing={billing} />}
      </Card>
    </MyAccountLayout>
  );
};

const Form = ({ billing }: { billing: BillingQuery["billing"] }) => {
  const router = useRouter();

  const { data: ordersQuery } = useOrdersQuery({
    orderTypes: [ORDER_TYPE_IDS.RECURRING],
    orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.CREATED],
  });

  const upcomingOrder = ordersQuery?.orders.find(() => true);

  const mealboxBasket = billing.baskets.find(isMealboxBasketType);

  const currentAddressId = mealboxBasket?.shippingAddressId;

  const currentAddressIsReceivingDelivery =
    upcomingOrder?.deliveryAddress.addressId === currentAddressId;

  const {
    isShowing: addressChangedAfterCutoffModalIsShowing,
    handleShow: handleShowAddressChangedAfterCutoffModal,
    handleCancel: cancelAddressChangedAfterCutoffModal,
    handleConfirm: handleConfirmAddressChangedAfterCutoffModal,
  } = useConfirmDialog();

  const defaultAddress = billing.addresses.find(
    (address) => address.addressId === currentAddressId,
  );

  const [selectedAddressId, setSelectedAddressId] = useState(
    defaultAddress?.addressId,
  );

  const selectedAddress = billing.addresses.find(
    (address) => address.addressId === selectedAddressId,
  );

  useEffect(() => {
    if (defaultAddress) {
      setSelectedAddressId(defaultAddress.addressId);
    }
  }, [defaultAddress]);

  const addressOptions = billing.addresses.map((address) => {
    const isAliasWasSetByCustomer = address?.address !== address?.name;
    return {
      label: isAliasWasSetByCustomer
        ? `${address.address} (${address.name})`
        : address.address,
      value: address.addressId,
    };
  });

  const [selectedTimeblockId, setSelectedTimeblockId] = useState(
    mealboxBasket?.timeblockId,
  );

  useEffect(() => {
    if (mealboxBasket) {
      setSelectedTimeblockId(mealboxBasket.timeblockId);
    }
  }, [mealboxBasket]);

  const postalCode = padPostalCode(selectedAddress?.postalCode || "");

  const { data: timeblocksDataQuery } = useTimeblocksQuery(
    {
      postalCode,
      timeblockTypeId: TIMEBLOCK_TYPE_IDS.NORMAL,
    },
    { skip: !isValidPostalCode(postalCode) },
  );
  const canDeliver =
    isValidPostalCode(postalCode) &&
    isNotEmptyArray(timeblocksDataQuery?.timeblocksData.timeblocks ?? []);

  const deliveryTimeslotOptions = createDeliveryTimeslotOptions(
    timeblocksDataQuery?.timeblocksData.timeblocks || [],
  );

  const [selectedDeliveryInterval, setSelectedDeliveryFrequency] = useState(
    mealboxBasket?.deliveryInterval,
  );
  useEffect(() => {
    setSelectedDeliveryFrequency(mealboxBasket?.deliveryInterval);
  }, [mealboxBasket]);

  const deliveryFrequencyOptions = createDeliveryFrequencyOptions();

  const [updateBillingAgreementMutation, { isLoading }] =
    useUpdateBillingAgreementMutation();

  const selectedAvailableTimeblockId =
    deliveryTimeslotOptions.find(
      (tbOption) => tbOption.value === selectedTimeblockId,
    )?.value || deliveryTimeslotOptions.find((tbOption) => tbOption)?.value;

  const handleSubmit = async () => {
    if (!selectedAddressId) {
      console.error("No selectedAddressId");
      return;
    }

    if (!selectedTimeblockId || !selectedAvailableTimeblockId) {
      console.error("No selectedTimeblockId");
      return;
    }

    if (!selectedDeliveryInterval) {
      console.error("No selectedDeliveryInterval");
      return;
    }

    try {
      await updateBillingAgreementMutation(
        convertFromBillingToUpdateBillingAgreement({
          ...billing,
          baskets: billing.baskets.map((b) => {
            if (!b.default) {
              return b;
            }

            return {
              ...b,
              shippingAddressId: selectedAddressId,
              timeblockId: selectedAvailableTimeblockId,
              deliveryInterval: selectedDeliveryInterval,
            };
          }),
        }),
      ).unwrap();

      router.push(BRAND_PATHS.DELIVERY_INFORMATION_PAGE);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <AddressChangedAfterCutoffModal
        show={addressChangedAfterCutoffModalIsShowing}
        onClose={cancelAddressChangedAfterCutoffModal}
        nextDeliveryDate={upcomingOrder && new Date(upcomingOrder.deliveryDate)}
        onConfirm={handleConfirmAddressChangedAfterCutoffModal}
      />

      <form
        className="flex flex-col gap-6"
        onSubmit={(e) => {
          e.preventDefault();
          if (upcomingOrder && currentAddressIsReceivingDelivery) {
            handleShowAddressChangedAfterCutoffModal(handleSubmit);
            return;
          }

          handleSubmit();
        }}
      >
        <div className="mt-6">
          <label className="text-sm">
            <strong>{intl.ADDRESS}:</strong>
          </label>
          <Dropdown
            options={addressOptions}
            value={selectedAddressId}
            onChange={setSelectedAddressId}
          />
        </div>

        {!canDeliver && (
          <Snackbar warning>
            <p>{intl.NO_DELIVERY_WARNING}</p>
          </Snackbar>
        )}

        {canDeliver && (
          <>
            <div>
              <label className="text-sm">
                <strong>{intl.WHEN_TO_DELIVER}:</strong>
              </label>
              <Dropdown
                options={deliveryTimeslotOptions}
                value={selectedAvailableTimeblockId}
                onChange={setSelectedTimeblockId}
              />
            </div>

            <div>
              <label className="text-sm">
                <strong>{intl.HOW_OFTEN_TO_DELIVER}:</strong>
              </label>
              <Dropdown
                options={deliveryFrequencyOptions}
                value={selectedDeliveryInterval}
                onChange={setSelectedDeliveryFrequency}
              />
            </div>
          </>
        )}

        <div className="flex flex-col gap-4 mt-10 lg:flex-row-reverse">
          <Button
            primary
            type="submit"
            loading={isLoading}
            disabled={!canDeliver}
          >
            {intl.SAVE_CHANGES}
          </Button>

          <Link
            passHref
            href={BRAND_PATHS.DELIVERY_INFORMATION_PAGE}
            legacyBehavior
          >
            <Button outlined disabled={isLoading}>
              {intl.CANCEL}
            </Button>
          </Link>
        </div>
      </form>
    </>
  );
};
